import * as React from "react";
import { type PropsWithChildren } from "react";
import { z } from "zod";

export enum Locale {
    DE = "de",
    EN = "en",
    ES_ES = "es-ES",
    FR = "fr",
    IT = "it",
    PT = "pt",
    SV_SE = "sv-SE",
}

export enum ConfigTheme {
    CFMOTO = "cfm",
    Default = "default",
    GasGas = "gg",
    Husqvarna = "hqv",
    KTM = "ktm",
    MVAgusta = "mva",
    SPORTCAR = "ksc",
    WPPerformanceSport = "wp",
}

export enum InitialRoute {
    CompatibilityCheck = "compatibilityCheck",
    Landing = "landing",
    Model = "model",
}

export const AttributeConfig = z
    .object({
        // z.guid() keeps zod 3's format-only check; zod 4's z.uuid() also enforces RFC version/variant bits
        apiKey: z.guid(),
        hasAddToCart: z.optional(z.boolean()),
        hasArticleInfos: z.optional(z.boolean()),
        showCompatibilityCheck: z.optional(z.boolean()),
        showPowerPartsButton: z.optional(z.boolean()),
        showSparePartKitsButton: z.optional(z.boolean()),
        locale: z.optional(z.enum(Locale)),
        region: z.optional(z.string()),
        brands: z.optional(z.string().array()),
        theme: z.optional(z.enum(ConfigTheme)),
        entryPoint: z.optional(
            z.object({
                view: z.enum(InitialRoute),
                scoped: z.optional(z.boolean()),
                modelArticleNumber: z.optional(z.string()),
            }),
        ),
        priceInfoDisclaimer: z.optional(z.string()),
        quantityInfoDisclaimer: z.optional(z.string()),
        trackingEnabled: z.optional(z.boolean()),
        trackingConfig: z.optional(
            z.object({
                fileHash: z.string(),
                trackDomain: z.string(),
                trackId: z.string(),
            }),
        ),
        wishlistEnabled: z.optional(z.boolean()),
        wishlistClientside: z.optional(z.boolean()),
        sendRequestEnabled: z.optional(z.boolean()),
    })
    .superRefine((val, ctx) => {
        if (val.trackingEnabled === true && val.trackingConfig === undefined) {
            ctx.addIssue({
                code: "custom",
                message: "trackingConfig should exist, because tracking is enabled",
            });
        }
    });
export type InputAttributeConfig = z.infer<typeof AttributeConfig>;

// override props, making them required instead of optional
export interface AttributeConfig extends InputAttributeConfig {
    hasAddToCart: boolean;
    hasArticleInfos: boolean;
    locale: Locale;
    region: string;
    sendRequestEnabled: boolean;
    setRegion: (value: string) => void;
    showCompatibilityCheck: boolean;
    showPowerPartsButton: boolean;
    showSparePartKitsButton: boolean;
    theme: ConfigTheme;
    trackingEnabled: boolean;
    wishlistClientside: boolean;
    wishlistEnabled: boolean;
}

export const defaultAttributeConfig: AttributeConfig = {
    apiKey: "",
    locale: Locale.EN,
    region: "EU",
    setRegion: () => {
        // empty
    },
    hasAddToCart: true,
    hasArticleInfos: true,
    showCompatibilityCheck: true,
    showPowerPartsButton: false,
    showSparePartKitsButton: false,
    theme: ConfigTheme.Default,
    trackingEnabled: false,
    wishlistEnabled: false,
    wishlistClientside: false,
    sendRequestEnabled: false,
};

const AttributeConfigContext = React.createContext<AttributeConfig>(defaultAttributeConfig);

const AttributeConfigProvider: React.FunctionComponent<PropsWithChildren & { config: AttributeConfig }> = ({ config, children }) => {
    const [region, setRegion] = React.useState<string>(config.region);

    return <AttributeConfigContext.Provider value={{ ...config, region, setRegion }}>{children}</AttributeConfigContext.Provider>;
};

export { AttributeConfigContext, AttributeConfigProvider };
